import { KeyRound } from "lucide-react";
import { useId } from "react";

import { ColocaInput } from "@/shared/components/forms/ColocaInput";
import { ColocaLabel } from "@/shared/components/forms/ColocaLabel";
import useTranslations from "@/shared/lang/useTranslations";

import type { BrebKeyResolutionState } from "../../hooks/useBrebKeyResolution";
import type { BrebKeyType } from "../../services/paymentsPayout.service";
import { normalizeBrebLookupValue, resolvedBrebKeyType } from "../../utils/brebKeyLookup";
import { BrebRecipientConfirmation } from "../DispersionForm/BrebRecipientConfirmation";

const KEY_TYPE_LABEL = {
  PHONE: "breb_key_type_phone",
  EMAIL: "breb_key_type_email",
  ALPHANUM: "breb_key_type_alphanum",
  NRIC: "breb_key_type_nric",
  MERCHANT: "breb_key_type_merchant",
} as const satisfies Record<BrebKeyType, string>;

export interface BrebKeyLookupFieldProps {
  value: string;
  onChange: (value: string) => void;
  resolution: BrebKeyResolutionState;
}

/** One field for any kind of key: the directory says which kind it is and who holds it. */
export function BrebKeyLookupField({ value, onChange, resolution }: BrebKeyLookupFieldProps) {
  const { t } = useTranslations("dispersions");
  const inputId = useId();

  const facts =
    resolution.status === "resolved"
      ? [
          t("send_key_type_line", {
            type: t(
              KEY_TYPE_LABEL[resolvedBrebKeyType(resolution.data.key_type, normalizeBrebLookupValue(value))],
            ),
          }),
          resolution.data.id_number
            ? [resolution.data.id_type, resolution.data.id_number].filter(Boolean).join(" ")
            : null,
        ]
          .filter(Boolean)
          .join(" · ")
      : null;

  return (
    <div className="space-y-2">
      <ColocaLabel htmlFor={inputId}>{t("send_key_label")}</ColocaLabel>
      <div className="relative">
        <KeyRound
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <ColocaInput
          id={inputId}
          value={value}
          onValueChange={(next) => onChange(next.slice(0, 80))}
          placeholder={t("send_key_placeholder")}
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          className="pl-9 font-mono"
        />
      </div>
      <p className="text-xs text-muted-foreground">{t("send_key_hint")}</p>
      <BrebRecipientConfirmation resolution={resolution} facts={facts} />
    </div>
  );
}
